import { supabase } from './supabase.js'
import { currentMonthKey, peso, shiftMonthKey } from './format.js'
import { getAccounts, moveBalance } from './worth.js'
import { addLink } from './notes.js'

/**
 * Income is what arrived and from where. Cashflow is what he plans to pay.
 * They are kept apart on purpose.
 *
 * Sources recognise themselves: a name typed once is remembered with its kind,
 * so the second time it completes and the kind comes with it. Nothing is
 * auto-created — a new source is still his tap.
 */

export const KINDS = [
  { key: 'salary',   label: 'Salary' },
  { key: 'gig',      label: 'Gig' },
  { key: 'business', label: 'Business' },
  { key: 'other',    label: 'Other' },
]

async function logIt(kind, summary, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind, summary, target_type: 'income_entry', target_id: targetId ?? null,
    })
  } catch {
    /* the log never fails the real write */
  }
}

/** First and last day of a month key, as ISO dates. */
export function monthRange(key) {
  const [y, m] = key.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  return [key + '-01', key + '-' + String(last).padStart(2, '0')]
}

export async function getSources() {
  const { data, error } = await supabase
    .from('income_sources')
    .select('*')
    .is('archived_at', null)
    .order('sort_order')
    .order('name')
  if (error) throw error
  return data ?? []
}

export async function addSource({ name, kind }) {
  const { data, error } = await supabase
    .from('income_sources')
    .insert({ name: name.trim(), kind: kind || 'other' })
    .select()
    .single()
  if (error) throw error
  return data
}

/**
 * The ledger. Every filter is applied in the query, not after, so the total
 * shown always matches exactly what is listed.
 */
export async function getEntries(filter = {}) {
  let q = supabase.from('income_entries').select('*').order('received_on', { ascending: false })

  if (filter.from) q = q.gte('received_on', filter.from)
  if (filter.to) q = q.lte('received_on', filter.to)
  if (filter.sourceId) q = q.eq('source_id', filter.sourceId)
  if (filter.min) q = q.gte('amount', filter.min)
  if (filter.max) q = q.lte('amount', filter.max)
  if (filter.text) q = q.ilike('note', '%' + filter.text.trim() + '%')

  const { data, error } = await q.limit(500)
  if (error) throw error
  return data ?? []
}

export async function addEntry({ sourceId, amount, receivedOn, note, payoutId }) {
  const { data, error } = await supabase
    .from('income_entries')
    .insert({
      source_id: sourceId,
      amount,
      received_on: receivedOn,
      note: note?.trim() || null,
      payout_id: payoutId ?? null,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('income.add', 'Income logged ' + peso(amount), data.id)
  return data
}

/*
 * Income straight into an account, without a payout line in Cashflow: the
 * payouts are his budget and this money is deliberately kept off it.
 *
 * Each side keeps its own record. The income stays in the Income ledger; the
 * account gets an ordinary deposit in its Net worth history, with a reason
 * that names the source; and a link joins the two so either can find the
 * other. The link is also how a later delete knows what to take back out.
 */
export async function depositIncome(entry, account, amount, sourceName) {
  const why = 'Income from ' + (sourceName || 'income')
  const { adjustment } = await moveBalance(account, 'up', amount, why)
  await addLink({ type: 'income_entry', id: entry.id }, { type: 'account_move', id: adjustment.id })
  await logIt('income.deposit', peso(amount) + ' deposited to ' + account.name, entry.id)
  return adjustment
}

/* The deposit made from this entry, if any, with the account it went into. */
export async function getDeposit(entryId) {
  const { data: links, error } = await supabase
    .from('links')
    .select('to_id')
    .eq('from_type', 'income_entry')
    .eq('from_id', entryId)
    .eq('to_type', 'account_move')
  if (error) throw error
  if (!links?.length) return null
  const { data: adj, error: e2 } = await supabase
    .from('account_adjustments')
    .select('id, account_id, old_balance, new_balance')
    .in('id', links.map((l) => l.to_id))
  if (e2) throw e2
  const accounts = await getAccounts(true)
  const deposits = (adj ?? []).map((a) => ({
    ...a,
    amount: Number(a.new_balance || 0) - Number(a.old_balance || 0),
    account: accounts.find((x) => x.id === a.account_id) ?? null,
  })).filter((d) => d.account)
  if (!deposits.length) return null
  return {
    account: deposits[0].account,
    amount: deposits.reduce((sum, d) => sum + d.amount, 0),
  }
}

export async function updateEntry(id, patch) {
  const { error } = await supabase.from('income_entries').update(patch).eq('id', id)
  if (error) throw error
}

/* Taking the money back out is its own withdrawal in the account's history,
   never an edit of the deposit, so Net worth shows both moves plainly. */
export async function deleteEntry(id, { reverse = null } = {}) {
  if (reverse) {
    await moveBalance(reverse.account, 'down', reverse.amount, 'Income entry removed, deposit reversed')
  }
  await supabase.from('links').delete()
    .eq('from_type', 'income_entry').eq('from_id', id)
  const { error } = await supabase.from('income_entries').delete().eq('id', id)
  if (error) throw error
  await logIt('income.delete', 'Income entry removed' + (reverse ? ', deposit reversed' : ''), id)
}

/**
 * This month against last, the salary-versus-everything-else split, and every
 * source ranked. Two queries, not one per source.
 */
export async function getDashboard(monthKey = currentMonthKey()) {
  const [thisFrom, thisTo] = monthRange(monthKey)
  const [lastFrom, lastTo] = monthRange(shiftMonthKey(monthKey, -1))

  const [sources, now, prev] = await Promise.all([
    getSources(),
    supabase.from('income_entries').select('amount, source_id')
      .gte('received_on', thisFrom).lte('received_on', thisTo),
    supabase.from('income_entries').select('amount')
      .gte('received_on', lastFrom).lte('received_on', lastTo),
  ])
  if (now.error) throw now.error
  if (prev.error) throw prev.error

  const byId = Object.fromEntries(sources.map((s) => [s.id, s]))
  const rows = now.data ?? []

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0)
  const lastTotal = (prev.data ?? []).reduce((s, r) => s + Number(r.amount || 0), 0)

  let salary = 0
  const perSource = {}
  for (const r of rows) {
    const amt = Number(r.amount || 0)
    const src = byId[r.source_id]
    if (src?.kind === 'salary') salary += amt
    perSource[r.source_id] = (perSource[r.source_id] || 0) + amt
  }

  const ranked = Object.entries(perSource)
    .map(([id, amount]) => ({
      id,
      amount,
      name: byId[id]?.name || 'Unknown',
      kind: byId[id]?.kind || 'other',
      pct: total > 0 ? Math.round((amount / total) * 100) : 0,
    }))
    .sort((a, b) => b.amount - a.amount)

  return {
    monthKey,
    total,
    lastTotal,
    /* Null rather than 0 when last month was empty — there is no percentage
       change from nothing, and showing one would be a lie. */
    change: lastTotal > 0 ? Math.round(((total - lastTotal) / lastTotal) * 100) : null,
    salary,
    other: total - salary,
    salaryPct: total > 0 ? Math.round((salary / total) * 100) : 0,
    ranked,
    sources,
  }
}

/** Exactly what is on screen, in the order it is on screen. */
export function toCsv(entries, sources) {
  const byId = Object.fromEntries(sources.map((s) => [s.id, s]))
  const esc = (v) => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const lines = [['Date', 'Source', 'Kind', 'Amount', 'Note'].join(',')]
  for (const e of entries) {
    const s = byId[e.source_id]
    lines.push([
      esc(e.received_on), esc(s?.name), esc(s?.kind), esc(e.amount), esc(e.note),
    ].join(','))
  }
  return lines.join('\n')
}
